import React from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useNavigate } from "react-router-dom";
import { IPetItemResponse } from "../../../../services/pet-list/pet-list.interface";
import { IPrequalificationInfoResponse } from "../../../../services/prequalification";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../../navigation/params";
import { PrequalBillingOptionCard } from "./PrequalBillingOptionCard";
import { PrequalResultLayout } from "./PrequalResultLayout";
import { logPrequalEvent } from "../prequalAnalytics";
import { useMinPlanPrices } from "../../../../hooks/useMinMonthlyPlanPrice";
import { usePrequalContinuation } from "../../../../hooks/usePrequalContinuation";

interface IPrequalResultScreenProps {
  result: IPrequalificationInfoResponse;
  pet?: IPetItemResponse;
  userFirstName?: string;
  userEmail?: string;
}

// Eligibility result shown right after the prequalify mutation settles
// (SGPD-515). ELIGIBLE per Figma 35828-52079, INELIGIBLE per 35825-51251
// (annual-only). Status ERROR never reaches this screen — the container
// routes it to PrequalErrorScreen.
export const PrequalResultScreen = ({
  result,
  pet,
  userFirstName,
  userEmail,
}: IPrequalResultScreenProps) => {
  const navigate = useNavigate();
  const { continueFromRecord } = usePrequalContinuation({ source: "prequal_result" });

  const isEligible = result.status === "ELIGIBLE";
  const petName = pet?.name;
  const email = result.email ?? userEmail;
  // Price clause appears once the catalog loads; the copy reads fine without.
  const { monthly: minMonthly, annual: minAnnual } = useMinPlanPrices(pet?.id);
  const monthlyDescription =
    "Cover your pet's annual care with 12 monthly payments" +
    `${minMonthly ? ` starting at $${minMonthly}/month` : ""}.`;
  const annualDescription =
    "Save when you make a one-time annual payment for the full cost of your plan today" +
    `${minAnnual ? `, starting at $${minAnnual} a year` : ""}.`;

  const handleSeePlans = () => {
    // "approved"/"declined" (not ELIGIBLE/INELIGIBLE) is the PRD's vocabulary
    // for prequal_see_plans, intentionally different from the raw status sent
    // on prequal_eligibility_result.
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_SEE_PLANS, {
      result: isEligible ? "approved" : "declined",
    });
    if (pet) {
      // Shared continuation routing (SGPD-475): honors a saved plan selection
      // and keeps the funnel gates.
      continueFromRecord(result, pet);
      return;
    }
    // Defensive fallback: the pet should always be resolvable in-session,
    // but a bare record still lets the user reach plan selection.
    navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${result.petId}`);
  };

  const handleMaybeLater = () => {
    navigate(PAGES_PATH.DASHBOARD);
  };

  const sharedProps = {
    // Fallback label matches the mobile result screen (SGPD-516).
    ctaLabel: petName ? `See ${petName}'s Options` : "See Plan Options",
    onCta: handleSeePlans,
    onMaybeLater: handleMaybeLater,
    emailNotice: isEligible && email ? `We emailed ${email} information on your eligibility.` : undefined,
  };

  if (!isEligible) {
    return (
      <PrequalResultLayout
        pillLabel="PRE-QUALIFIED"
        title={`You qualify for annual billing${userFirstName ? `, ${userFirstName}` : ""}.`}
        subtitle={
          "The monthly billing option isn't available to you at this time. " +
          "Questions? Contact our support team."
        }
        {...sharedProps}
      >
        <PrequalBillingOptionCard
          title="Annual"
          icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
          description={annualDescription}
          highlighted
        />
      </PrequalResultLayout>
    );
  }

  return (
    <PrequalResultLayout
      pillLabel="PRE-APPROVED"
      title={`You're eligible${userFirstName ? ` ${userFirstName}` : ""}!`}
      subtitle={
        `Both billing options are unlocked for ${petName ?? "your pet"}'s TotalCare Plan - ` +
        "pick the one that fits your budget on the next step."
      }
      {...sharedProps}
    >
      <PrequalBillingOptionCard
        title="Monthly"
        icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />}
        description={monthlyDescription}
        badgeLabel="Most Popular"
        showScratchLockup
      />
      <PrequalBillingOptionCard
        title="Annual"
        icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
        description={annualDescription}
      />
    </PrequalResultLayout>
  );
};
